import { useState } from "react";

type Props = {
  /** Called when the user presses Enter on a numeric input. The handler
   *  is expected to look up the card for that VID and open both the
   *  slideout and Quote Editor (see App.tsx's onOpenForVisit). */
  onOpen: (vid: number) => Promise<void> | void;
};

/** Tight little "Open #" input. Pipeline + Cleaner Earnings boards use
 *  this so Andzelika can type an order number and jump straight to the
 *  card slideout + Quote Editor. Accepts VID as plain digits; tolerant
 *  of common prefixes ("#30879", "VID-30879"). */
export function OpenOrderInput({ onOpen }: Props): JSX.Element {
  const [value, setValue] = useState("");
  const [pending, setPending] = useState(false);
  const [errorAt, setErrorAt] = useState<number>(0);

  async function submit(): Promise<void> {
    const numeric = value.replace(/\D+/g, "");
    const vid = Number(numeric);
    if (!Number.isFinite(vid) || vid <= 0) {
      setErrorAt(Date.now());
      return;
    }
    setPending(true);
    try {
      await onOpen(vid);
      setValue("");
    } catch {
      setErrorAt(Date.now());
    } finally {
      setPending(false);
    }
  }

  const flashing = Date.now() - errorAt < 1500;

  return (
    <div className="relative flex items-center">
      <span className="pointer-events-none absolute left-2 text-[11px] uppercase tracking-wider text-ink-50/60">
        #
      </span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder="Order"
        value={value}
        disabled={pending}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
        }}
        className={
          "w-24 rounded-md border bg-ink-800 py-1 pl-5 pr-2 text-[12px] tabular-nums text-ink-50 placeholder:text-ink-50/40 " +
          (flashing
            ? "border-red-500/70 focus:border-red-500 focus:outline-none"
            : "border-ink-500 focus:border-slack-aubergineLight focus:outline-none")
        }
        title="Type an order number (VID) and press Enter to open it"
      />
    </div>
  );
}
